function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useState, useRef } from 'react';
import { compact, debounce, isEqual } from 'lodash-es';
import { InvoiceUpdatedType, PusherEventName } from 'anchor-scout-common/types/pusher';
import { PageContentHeader, TableListProvider, TableListHeader, FilterPickerGroup, Searchbox, useMediaQueries, MultiSelectFilterPicker, SingleSelectFilterPicker, Spinner, PaginationControl, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { AccountingStatusFilter } from "../../business-logic/qboStatusFilterLogic";
import { ErrorText } from "../../constants";
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { useUnmarkPaidExternally } from "../../hooks/invoice/useUnmarkPaidExternally";
import { useUnvoidInvoice } from "../../hooks/invoice/useUnvoidInvoice";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useOnMount } from "../../hooks/useOnMount";
import { useRestfulWrapper } from "../../hooks/useRestfulWrapper";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { handlePromise } from "../../infra/utils";
import { invoiceModel } from "../../models";
import { useSubscribeToBillingObservable } from "../../providers/BillingObservableProvider";
import { useSubscribeToInvoiceChangesObservable } from "../../providers/InvoiceChangesObservableProvider";
import { BusinessChannelEventBinder } from "../../pusher/BusinessChannelEventBinder";
import { selectors } from "../../store/state-selectors";
import { useAppConfig } from "../../store/store-context";
import { getURLWithFullPageInvoice } from "../FullPageInvoice/FullPageInvoice.utils";
import { NoFilteredInvoices } from "../InvoicesEmptyStates/InvoicesEmptyStates";
import { DateFilterPicker } from "./DateFilterPicker";
import styles from "./InvoicesTable.module.scss";
import { applyOptimisticUpdate, getPaymentStatusFilterOptions, getInvoiceStatusFilterOptions, getAccountingStatusFilterOptions, mapSortAndFiltersRequestParams, getTableDefs } from "./InvoicesTable.utils";
import InvoicesTableRow from "./InvoicesTableRow";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const defaultFilters = {
  date: 'all-time',
  status: [],
  paymentStatus: [],
  search: '',
  qbo: AccountingStatusFilter.All,
  xero: AccountingStatusFilter.All
};
export function InvoicesTableWithData(props) {
  const {
    filtersClassname,
    filters,
    columnDefs,
    headerItems,
    hideDueDate,
    sort,
    loading,
    pageNumber,
    isEmptyFilters,
    onSortChange,
    onFilterChange,
    onResetFilters,
    retry,
    onPageNumberChange
  } = props;
  const {
    statusFilterCounters,
    paymentStatusFilterCounters,
    qboStatusFilterCounters,
    xeroStatusFilterCounters,
    total
  } = props.data;
  const {
    search,
    status: statusFilter,
    paymentStatus: paymentStatusFilter,
    date: dateFilter,
    qbo: qboStatusFilter,
    xero: xeroStatusFilter
  } = filters;
  const {
    paginationPageSize
  } = useAppConfig();
  const {
    XERO_INTEGRATION,
    UNVOID_INVOICE,
    UNMARK_PAID_EXTERNALLY
  } = useFeatureFlags();
  const [invoicesFromServer, setInvoicesFromServer] = useState(props.data.invoices);
  useEffect(() => setInvoicesFromServer(props.data.invoices), [props.data.invoices]);
  const {
    getNow
  } = useIoContext();
  const isVendor = useStore(selectors.user.isVendor);
  const {
    logBI,
    fetchIntegrationsSummary
  } = useStoreActions();
  const {
    startUnvoidInvoice,
    finishUnvoidInvoice
  } = useUnvoidInvoice();
  const {
    startUnmarkPaidExternally,
    finishUnmarkPaidExternally
  } = useUnmarkPaidExternally();
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11451);
  const invoiceStatusFilterOptions = getInvoiceStatusFilterOptions(statusFilterCounters);
  const paymentStatusFilterOptions = getPaymentStatusFilterOptions(isVendor, paymentStatusFilterCounters);
  const qboStatusFilterOptions = getAccountingStatusFilterOptions(qboStatusFilterCounters);
  const xeroStatusFilterOptions = getAccountingStatusFilterOptions(xeroStatusFilterCounters);
  useSubscribeToBillingObservable(() => retry());
  useSubscribeToInvoiceChangesObservable(async _ref => {
    let {
      optimisticUpdate
    } = _ref;
    if (optimisticUpdate) {
      setInvoicesFromServer(inv => applyOptimisticUpdate(inv, optimisticUpdate, getNow()));
      retry({
        disableFetchInvoices: true
      });
    } else {
      retry();
    }
  });
  useOnMount(() => {
    if (showQboIntegration) {
      fetchIntegrationsSummary();
    }
  });
  const handleSortChange = sort => {
    logBI(11450, {
      column: sort ? headerItems[sort.colIndex].id : 'none'
    });
    onSortChange(sort);
  };
  function applyInvoiceFromPusherEvent(invoice) {
    setInvoicesFromServer(invoices => {
      return invoices.map(i => {
        if (i.id === invoice.id) {
          return invoiceModel.fromDTO(isVendor, invoice);
        }
        return i;
      });
    });
  }
  return /*#__PURE__*/_jsxs(TableListProvider, {
    hasActionColumn: true,
    columnDefs: columnDefs,
    sort: sort,
    onSortChange: handleSortChange,
    children: [/*#__PURE__*/_jsx(BusinessChannelEventBinder, {
      source: "scout",
      eventName: PusherEventName.InvoiceUpdated,
      callback: payload => {
        if (payload.type === InvoiceUpdatedType.RefundFailed) {
          applyInvoiceFromPusherEvent(payload.invoice);
        } else if (payload.type === InvoiceUpdatedType.Unvoided && UNVOID_INVOICE) {
          applyInvoiceFromPusherEvent(payload.invoice);
          finishUnvoidInvoice(payload.invoice.id);
        } else if (payload.type === InvoiceUpdatedType.UnmarkedAsPaidExternally && UNMARK_PAID_EXTERNALLY) {
          applyInvoiceFromPusherEvent(payload.invoice);
          finishUnmarkPaidExternally(payload.invoice.id);
        }
      }
    }), /*#__PURE__*/_jsxs(PageContentHeader, {
      children: [/*#__PURE__*/_jsx("div", {
        className: filtersClassname,
        children: /*#__PURE__*/_jsxs(FilterPickerGroup, {
          "data-testid": "invoices-dashboard-filter-picker-group",
          onClearClick: onResetFilters,
          disableClear: isEmptyFilters,
          children: [/*#__PURE__*/_jsx(Searchbox, {
            registerHotkey: true,
            value: search,
            "data-testid": "invoices-dashboard-search",
            onChange: event => {
              const search = event.target.value;
              onFilterChange({
                search
              }, {
                withDebounce: true
              });
              debouncedLogSearchToBI.onChange(search);
            },
            onClear: () => {
              onFilterChange({
                search: ''
              });
              debouncedLogSearchToBI.onClear();
            }
          }), /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
            "data-testid": "invoices-status-filter-picker",
            value: statusFilter,
            options: invoiceStatusFilterOptions,
            label: "Invoice status",
            onChange: status => {
              onFilterChange({
                status
              });
              if (status.length) logBI(11452, {
                status
              });
            }
          }), /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
            "data-testid": "invoices-payment-status-filter-picker",
            value: paymentStatusFilter,
            options: paymentStatusFilterOptions,
            label: "Payment status",
            onChange: paymentStatus => onFilterChange({
              paymentStatus
            })
          }), /*#__PURE__*/_jsx(DateFilterPicker, {
            value: dateFilter,
            onChange: date => {
              onFilterChange({
                date
              });
              if (date !== defaultFilters.date) {
                logBI(11453, {
                  date
                });
              }
            }
          }), showQboIntegration && qboStatusFilterOptions.length > 0 && /*#__PURE__*/_jsx(SingleSelectFilterPicker, {
            "data-testid": "invoices-qbo-filter",
            label: "QuickBooks Online sync status",
            value: qboStatusFilter,
            options: qboStatusFilterOptions,
            onChange: option => option && onFilterChange({
              qbo: option.value
            })
          }), XERO_INTEGRATION && showXeroIntegration && xeroStatusFilterOptions.length > 0 && /*#__PURE__*/_jsx(SingleSelectFilterPicker, {
            "data-testid": "invoices-xero-filter",
            label: "Xero sync status",
            value: xeroStatusFilter,
            options: xeroStatusFilterOptions,
            onChange: option => option && onFilterChange({
              xero: option.value
            })
          })]
        })
      }), /*#__PURE__*/_jsx(TableListHeader, {
        "data-testid": "invoices-table-header",
        items: headerItems
      })]
    }), loading ? /*#__PURE__*/_jsx(Spinner, {
      centeredInBox: true,
      centerBoxHeight: 200
    }) : invoicesFromServer.length > 0 ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [invoicesFromServer.map((invoice, index) => /*#__PURE__*/_jsx(InvoicesTableRow, {
        invoice: invoice,
        index: index,
        hideDueDate: hideDueDate,
        showQboIntegration: showQboIntegration,
        showXeroIntegration: showXeroIntegration,
        to: getURLWithFullPageInvoice({
          invoiceId: invoice.id,
          relationshipId: invoiceModel.relationshipId(invoice)
        }),
        retry: retry,
        onUnvoidInvoice: () => startUnvoidInvoice(invoice),
        onUnmarkPaidExternally: () => startUnmarkPaidExternally(invoice)
      }, invoice.id)), /*#__PURE__*/_jsx("div", {
        className: styles.paginationControls,
        children: /*#__PURE__*/_jsx(PaginationControl, {
          pageSize: paginationPageSize,
          pageNumber: pageNumber,
          total: total,
          onPageNumberChange: onPageNumberChange
        })
      })]
    }) : /*#__PURE__*/_jsx(NoFilteredInvoices, {
      resetFilters: onResetFilters
    })]
  });
}
export default function InvoicesTable(props) {
  const {
    relationshipId,
    filtersClassname,
    onRetry,
    renderLoader,
    renderError,
    renderChild: _renderChild,
    renderNoInvoices
  } = props;
  const {
    getNow,
    downloadCsv
  } = useIoContext();
  const {
    paginationPageSize,
    inputDebounceTime,
    exportCsvFilename
  } = useAppConfig();
  const [pageNumber, _setPageNumber] = useState(1);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
  const {
    smallScreen
  } = useMediaQueries();
  const hideDueDate = smallScreen;
  const {
    api
  } = useStoreActions();
  const {
    showErrorToast
  } = useOverlayContext();
  const [exportingCsv, setExportingCsv] = useState(false);
  const defaultSort = useMemo(() => ({
    colIndex: 0,
    sort: 'desc'
  }), []);
  const {
    columnDefs,
    headerItems,
    sortIds
  } = useMemo(() => getTableDefs({
    showQboIntegration,
    hideDueDate,
    showXeroIntegration
  }), [showQboIntegration, hideDueDate, showXeroIntegration]);
  const {
    sort,
    changeSort: _changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    urlState,
    changeUrlState: _changeUrlState
  } = useStateFromUrl({
    status: {
      multi: true,
      defaultValue: defaultFilters.status
    },
    paymentStatus: {
      multi: true,
      defaultValue: defaultFilters.paymentStatus
    },
    date: {
      defaultValue: defaultFilters.date
    },
    search: {
      defaultValue: defaultFilters.search
    },
    qbo: {
      defaultValue: defaultFilters.qbo
    },
    xero: {
      defaultValue: defaultFilters.xero
    }
  });
  const urlStateRef = useRef(urlState);
  const sortRef = useRef(sort);
  const pageNumberRef = useRef(pageNumber);
  const {
    renderRestfulState,
    refresh
  } = useRestfulWrapper({
    fetch: () => api.invoice.getAll(_objectSpread(_objectSpread({}, mapSortAndFiltersRequestParams(_objectSpread(_objectSpread({}, urlStateRef.current), {}, {
      sort: sortRef.current,
      now: getNow(),
      sortIds
    }))), {}, {
      relationshipId,
      pageSize: paginationPageSize,
      pageNumber: pageNumberRef.current
    }))
  });
  useOnMount(() => {
    return () => refreshWithDebounce.cancel();
  });
  const refreshWithDebounce = useMemo(() => {
    const debounceTime = inputDebounceTime != null ? inputDebounceTime : 500;
    return debounce(refresh, debounceTime);
  }, []);
  function changeUrlState(newUrlState) {
    _changeUrlState(newUrlState);
    urlStateRef.current = _objectSpread(_objectSpread({}, urlStateRef.current), newUrlState);
  }
  function changeSort(newSort) {
    _changeSort(newSort);
    sortRef.current = newSort;
  }
  function setPageNumber(pageNumber) {
    _setPageNumber(pageNumber);
    pageNumberRef.current = pageNumber;
  }
  function handleFilterChange(newUrlState, opts) {
    changeUrlState(newUrlState);
    setPageNumber(1);
    scheduleRefresh(opts);
  }
  function handleSortChange(sort) {
    changeSort(sort);
    setPageNumber(1);
    scheduleRefresh();
  }
  function handlePageNumberChange(pageNumber) {
    setPageNumber(pageNumber);
    scheduleRefresh();
  }
  function scheduleRefresh() {
    let {
      withDebounce
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    refreshWithDebounce();
    !withDebounce && refreshWithDebounce.flush();
  }
  return renderRestfulState({
    renderLoader,
    renderError,
    renderChild: _ref2 => {
      let {
        data,
        loading,
        refresh
      } = _ref2;
      const isEmptyFilters = isEqual(urlState, defaultFilters);
      if (!data.invoices.length && !loading && renderNoInvoices && isEmptyFilters) {
        return renderNoInvoices();
      }
      const tableContent = /*#__PURE__*/_jsx(InvoicesTableWithData, {
        data: data,
        columnDefs: columnDefs,
        headerItems: headerItems,
        hideDueDate: hideDueDate,
        sort: sort,
        filtersClassname: filtersClassname,
        filters: urlState,
        isEmptyFilters: isEmptyFilters,
        loading: loading,
        pageNumber: pageNumber,
        onSortChange: handleSortChange,
        onFilterChange: handleFilterChange,
        onResetFilters: () => handleFilterChange(defaultFilters),
        onPageNumberChange: handlePageNumberChange,
        retry: async function retry() {
          let {
            disableFetchInvoices
          } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
          await Promise.all(compact([!disableFetchInvoices && refresh(), onRetry && onRetry()]));
        }
      });
      return _renderChild ? _renderChild({
        data,
        loading,
        tableContent,
        exportingCsv,
        refresh,
        getRows: () => data.invoices,
        async exportCsv() {
          setExportingCsv(true);
          const params = mapSortAndFiltersRequestParams(_objectSpread(_objectSpread({}, urlStateRef.current), {}, {
            sort: sortRef.current,
            sortIds,
            now: getNow()
          }));
          const result = await handlePromise(api.invoice.exportToCsv(params));
          if (result.success) {
            const filename = exportCsvFilename || 'exported-invoices.csv';
            downloadCsv(result.data, filename);
          } else {
            showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'export-invoices-error');
          }
          setExportingCsv(false);
        }
      }) : tableContent;
    }
  });
}